import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { RouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import generateDefaultProposalName from 'global/utils/generateDefaultProposalName';
import BulletTabsDelegate, { BulletTabStep } from 'store/general/bulletTabsDelegate';
import FavoriteProposalStepController from 'store/modules/initiate-quote/favoriteProposalStepController';
import ProposalExperienceStepController from 'store/modules/initiate-quote/proposalExperienceStepController';
import {
  ProposalExperience,
  ProposalExperienceAccessLimitations,
  ProposalExperiencePayload,
  ProposalExperiencePolicy,
} from 'store/modules/initiate-quote/types';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import BasicInfoStepController from './basicInfoStepController';

interface DomainParams {
  groupId: string;
}

export default class AltusInitiateProposalPageStore {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public domainParams: DomainParams;
  public initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public bulletTabsDelegate: BulletTabsDelegate;

  // Step controllers
  public basicInfoStepController: BasicInfoStepController;
  public proposalExperienceStepController: ProposalExperienceStepController;
  public favoriteProposalStepController: FavoriteProposalStepController;

  public get proposalExperience(): ProposalExperience | null {
    return this.proposalExperienceStepController.proposalExperience;
  }

  private get _stepControllers(): BulletTabStep[] {
    const steps: BulletTabStep[] = [this.basicInfoStepController, this.proposalExperienceStepController];
    if (this.proposalExperience === 'FAVORITE') {
      steps.push(this.favoriteProposalStepController);
    }
    return steps;
  }

  private _getExperiencePolicy = (): ProposalExperiencePolicy => {
    const newBusinessExperiences: ProposalExperience[] = ['SHELF', 'FAVORITE'];
    const renewalExperiences: ProposalExperience[] = ['RENEWAL_SHELF'];
    const isCelticsUser = celticsRights(this._rootStore.userStore.userRoles);
    const celticsAccessLimitation = isCelticsUser ? 'Management Only' : '';

    if (isCelticsUser) {
      newBusinessExperiences.push('CUSTOM');
      renewalExperiences.push('RENEWAL_CUSTOM');
    }

    return {
      newBusinessExperiences,
      renewalExperiences,
      getExperienceAccessLimitations: (): ProposalExperienceAccessLimitations =>
        celticsAccessLimitation
          ? {
              CUSTOM: celticsAccessLimitation,
              RENEWAL_CUSTOM: celticsAccessLimitation,
            }
          : {},
    };
  };

  private _getPayload = (): ProposalExperiencePayload => {
    const { effectiveDate, zip, state, county, proposalName } = this.basicInfoStepController.form.getFormDataObject();

    return {
      proposalName,
      browsingCriteria: {
        effectiveDate,
        zipCode: zip,
        state,
        county,
        shortName: {},
      },
    };
  };

  public async getInitialData(): Promise<void> {
    this.initiateQuoteFlowDelegate.setIsInitialDataLoading(true);
    await this.initiateQuoteFlowDelegate.getFlowInitialData();
    runInAction(() => {
      const address = this.initiateQuoteFlowDelegate.groupModule.group.contactInfo.primaryAddress;

      this.basicInfoStepController.form.populateFields({
        proposalName: generateDefaultProposalName(this.initiateQuoteFlowDelegate.groupModule.groupName),
        zip: address.zip || '',
        state: address.state || '',
        county: address.county || '',
      });
      this.initiateQuoteFlowDelegate.setIsInitialDataLoading(false);
    });
  }

  public exitFromFirstStep(): void {
    redirectToRoute(RouteV2Id.GROUP_HUB, { groupId: this.domainParams.groupId });
  }

  constructor(params: DomainParams) {
    this.domainParams = params;
    this.initiateQuoteFlowDelegate = new InitiateQuoteFlowDelegate({
      domainParams: { groupId: params.groupId, nextPageId: RouteV2Id.QUOTE_CHECKLIST },
    });
    this.basicInfoStepController = new BasicInfoStepController(this.initiateQuoteFlowDelegate);
    this.proposalExperienceStepController = new ProposalExperienceStepController({
      initiateQuoteFlowDelegate: this.initiateQuoteFlowDelegate,
      getPayload: this._getPayload,
      getPolicy: this._getExperiencePolicy,
    });
    this.favoriteProposalStepController = new FavoriteProposalStepController(
      this.initiateQuoteFlowDelegate,
      this._getPayload,
    );

    this.bulletTabsDelegate = new BulletTabsDelegate({
      getStepControllers: (): BulletTabStep[] => this._stepControllers,
      exitFromFirstStep: (): void => this.exitFromFirstStep(),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const AltusInitiateProposalPageStoreContext = createContext(
  new AltusInitiateProposalPageStore({
    groupId: '',
  }),
);

export const useAltusInitiateProposalPageStore = (): AltusInitiateProposalPageStore => {
  return useContext(AltusInitiateProposalPageStoreContext);
};

export { AltusInitiateProposalPageStoreContext };
